import React, { useState, useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  Box, Typography, Button, Grid, Card, IconButton, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Alert as MuiAlert, Snackbar,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { useThemeMode, MONO } from '../../context/ThemeContext';
import { useQueryClient } from '@tanstack/react-query';
import { useCommodities, useLocalPrices, usePortfolio, useCategories } from '../../services/queries';
import { addHolding, removeHolding } from '../../services/account';
import { limitsFor, atLimit, usageLabel, isPlanLimit } from '../../utils/planLimits';
import { PageHeader, LoadingSkeleton, EmptyState, StatCard, FilterSelect, ConfirmDialog, DeltaChip, UpdatedStamp } from '../../components/common';
import { useRecipes } from '../../theme/recipes';
import { activeCommodities, isCommodityActive } from '../../utils/catalogStatus';

// Valuation basis for a commodity: on its latest collection date, take the
// MOST COMMON bag size among that day's records and average its per-bag
// price across the markets that reported it (averaging mixed bag sizes
// would price apples and oranges together). The 7-day change compares the
// previous collection date on the same bag-size basis. Returns nulls when
// no data exists - the UI shows '-', never an invented figure.
const priceSnapshot = (commodityId, localPrices) => {
  const rows = (localPrices || []).filter((p) => p.commodityId === commodityId);
  const empty = { current: null, change: null, bagSize: null, markets: 0, date: null };
  if (rows.length === 0) return empty;

  const dates = [...new Set(rows.map((p) => p.date))].sort();
  const basisFor = (date, forcedBagSize) => {
    const day = rows.filter((p) => p.date === date && p.price != null);
    if (day.length === 0) return null;
    let bagSize = forcedBagSize;
    if (bagSize == null) {
      const counts = {};
      day.forEach((p) => { counts[p.bagSize] = (counts[p.bagSize] || 0) + 1; });
      bagSize = Number(Object.keys(counts).sort((a, b) => counts[b] - counts[a])[0]);
    }
    const subset = day.filter((p) => p.bagSize === bagSize);
    if (subset.length === 0) return null;
    const avg = subset.reduce((s, p) => s + p.price, 0) / subset.length;
    return { avg, bagSize, markets: new Set(subset.map((p) => p.marketId)).size };
  };

  const latestDate = dates[dates.length - 1];
  const latest = basisFor(latestDate);
  if (!latest) return empty;

  let change = 0;
  if (dates.length >= 2) {
    const prev = basisFor(dates[dates.length - 2], latest.bagSize);
    if (prev && prev.avg) change = ((latest.avg - prev.avg) / prev.avg) * 100;
  }
  return { current: Math.round(latest.avg), change, bagSize: latest.bagSize, markets: latest.markets, date: latestDate };
};

function Portfolio() {
  const { palette } = useThemeMode();
  const recipes = useRecipes();
  const queryClient = useQueryClient();
  const { data: commodities = [], isLoading: cLoading } = useCommodities();
  const { data: localPrices = [], isLoading: lpLoading } = useLocalPrices();
  const { data: items = [], isLoading: pLoading } = usePortfolio();

  // Mirrors the server cap so the page can say what is left before someone
  // fills in the form, rather than refusing them after.
  const { user: planUser } = useAuth();
  const planLimits = limitsFor(planUser);
  const holdingsFull = atLimit(items.length, planLimits.maxHoldings);
  const planUsage = usageLabel(items.length, planLimits.maxHoldings, 'holdings');
  const { data: commodityCategories = [] } = useCategories();
  const loading = cLoading || lpLoading || pLoading;
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmId, setConfirmId] = useState(null);
  const [toast, setToast] = useState(null);
  const [form, setForm] = useState({ commodityId: '', quantity: '' });

  // Compact variant of the primary CTA recipe for header/dialog buttons
  const ctaSx = { ...recipes.primaryBtnSx, px: 2.5, py: 0.9, fontSize: '0.875rem', textTransform: 'none' };

  const pickerCommodities = useMemo(
    () => activeCommodities(commodities, commodityCategories),
    [commodities, commodityCategories]
  );

  const openDialog = () => {
    setForm({ commodityId: pickerCommodities[0]?.id || '', quantity: '' });
    setDialogOpen(true);
  };

  const save = async () => {
    const qty = Number(form.quantity);
    if (!qty || qty <= 0) { setToast({ severity: 'error', msg: 'Enter a valid quantity (bags).' }); return; }
    try {
      await addHolding({ commodityId: form.commodityId, quantity: qty });
      queryClient.invalidateQueries({ queryKey: ['portfolio'] });
      setDialogOpen(false);
      setForm({ commodityId: pickerCommodities[0]?.id || '', quantity: '' });
      setToast({ severity: 'success', msg: 'Added to portfolio.' });
    } catch (e) {
      setToast({
        severity: isPlanLimit(e) ? 'warning' : 'error',
        msg: e?.message || 'Could not add holding.',
      });
    }
  };
  const remove = async (id) => {
    try {
      await removeHolding(id);
      queryClient.invalidateQueries({ queryKey: ['portfolio'] });
      setToast({ severity: 'info', msg: 'Removed from portfolio.' });
    } catch (e) {
      setToast({ severity: 'error', msg: e?.message || 'Could not remove holding.' });
    }
  };

  const enriched = useMemo(() => items.map((i) => {
    const c = commodities.find((x) => x.id === i.commodityId);
    const snap = priceSnapshot(i.commodityId, localPrices);
    const value = snap.current != null ? snap.current * i.quantity : null;
    const prevValue = value != null && snap.change ? value / (1 + snap.change / 100) : value;
    const pnl = value != null ? value - prevValue : null;
    const retired = c ? !isCommodityActive(c, commodityCategories) : false;
    return { ...i, commodity: c, snap, value, pnl, retired };
  }), [items, commodities, commodityCategories, localPrices]);

  const totals = useMemo(() => {
    const priced = enriched.filter((e) => e.value != null);
    const totalValue = priced.reduce((sum, e) => sum + e.value, 0);
    const totalPnl = priced.reduce((sum, e) => sum + e.pnl, 0);
    const pct = totalValue > 0 ? (totalPnl / (totalValue - totalPnl)) * 100 : 0;
    return { totalValue, totalPnl, pct };
  }, [enriched]);

  // Current price for the add-holding dialog's selection, plus a live
  // estimated value as the quantity is typed.
  const dialogSnap = useMemo(
    () => (form.commodityId ? priceSnapshot(form.commodityId, localPrices) : null),
    [form.commodityId, localPrices]
  );
  const estimatedValue =
    dialogSnap?.current != null && Number(form.quantity) > 0
      ? dialogSnap.current * Number(form.quantity)
      : null;

  const confirmTarget = confirmId ? enriched.find((e) => e.id === confirmId) : null;

  if (loading) {
    return <Box sx={{ p: 1 }}><LoadingSkeleton variant="page" /></Box>;
  }

  return (
    <Box>
      <PageHeader
        eyebrow="Premium"
        title="Portfolio"
        subtitle="Track the market value and 7-day performance of commodities you hold"
        breadcrumbs={[
          { label: 'Home', path: '/' },
          { label: 'Portfolio' }
        ]}
        actions={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {planUsage && (
              <Typography sx={{ fontSize: '0.78rem', color: holdingsFull ? palette.warning : palette.textMuted }}>
                {planUsage}
              </Typography>
            )}
            {holdingsFull ? (
              <Button
                component={RouterLink}
                to="/app/subscription"
                variant="contained"
                disableElevation
                startIcon={<Icon icon="ph:arrow-up-right" width={16} height={16} />}
                sx={ctaSx}
              >
                Upgrade for more
              </Button>
            ) : (
              <Button
                variant="contained"
                disableElevation
                startIcon={<Icon icon="ph:plus" width={16} height={16} />}
                onClick={openDialog}
                sx={ctaSx}
              >
                Add holding
              </Button>
            )}
          </Box>
        }
      />

      {/* Summary cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <StatCard
            label="Total Value"
            value={`₦${totals.totalValue.toLocaleString()}`}
            sub={`${enriched.length} holding${enriched.length === 1 ? '' : 's'}`}
            tone="accent"
            icon={<Icon icon="ph:wallet" width={20} height={20} />}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard
            label="7-day P/L"
            value={`${totals.totalPnl >= 0 ? '+' : ''}₦${Math.round(totals.totalPnl).toLocaleString()}`}
            valueColor={totals.totalPnl >= 0 ? palette.accent : palette.danger}
            sub={`${totals.pct >= 0 ? '+' : ''}${totals.pct.toFixed(2)}% vs last week`}
            tone={totals.totalPnl >= 0 ? 'accent' : 'danger'}
            icon={<Icon icon={totals.totalPnl >= 0 ? 'ph:trend-up' : 'ph:trend-down'} width={20} height={20} />}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          {(() => {
            // Only holdings that actually have price data qualify - a holding
            // with no records has change: null and must not crash this card.
            const priced = enriched.filter((e) => e.snap.change != null);
            const biggest = priced.length
              ? [...priced].sort((a, b) => Math.abs(b.snap.change) - Math.abs(a.snap.change))[0]
              : null;
            return (
              <StatCard
                label="Biggest Mover"
                value={biggest ? `${biggest.snap.change >= 0 ? '+' : ''}${biggest.snap.change.toFixed(2)}%` : '-'}
                valueColor={biggest ? (biggest.snap.change >= 0 ? palette.accent : palette.danger) : palette.textMuted}
                sub={biggest?.commodity?.name || (enriched.length ? 'No price data yet' : 'No holdings yet')}
                tone="info"
                icon={<Icon icon={biggest && biggest.snap.change < 0 ? 'ph:trend-down' : 'ph:trend-up'} width={20} height={20} />}
              />
            );
          })()}
        </Grid>
      </Grid>

      {/* Table */}
      {enriched.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Icon icon="ph:wallet" width={32} height={32} />}
            title="No holdings yet"
            description="Add the commodities you currently hold to track value and performance."
            actionLabel="Add holding"
            onAction={openDialog}
          />
        </Card>
      ) : (
        <TableContainer component={Card}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {[
                  { label: 'Commodity', align: 'left' },
                  { label: 'Quantity (bags)', align: 'right' },
                  { label: 'Current Price / bag', align: 'right' },
                  { label: 'Value', align: 'right' },
                  { label: '7-day Change', align: 'right' },
                  { label: 'P/L', align: 'right' },
                  { label: 'Action', align: 'right' },
                ].map((col) => (
                  <TableCell key={col.label} align={col.align} sx={recipes.headCellSx}>
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {enriched.map((e) => (
                <TableRow key={e.id} hover sx={recipes.bodyRowSx}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography sx={{ fontWeight: 600, fontSize: '0.85rem' }}>{e.commodity?.name}</Typography>
                      {e.retired && <Chip label="RETIRED" size="small" sx={{ ml: 0.75, height: 16, fontSize: '0.55rem', fontWeight: 700, letterSpacing: '0.06em', bgcolor: palette.surfaceHover, color: palette.textMuted }} />}
                    </Box>
                    <Typography sx={{ fontSize: '0.7rem', color: palette.textMuted }}>{e.commodity?.canonicalName}</Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ fontFamily: MONO }}>{e.quantity.toLocaleString()}</TableCell>
                  <TableCell align="right">
                    {e.snap.current != null ? (
                      <>
                        <Typography sx={{ fontFamily: MONO, fontSize: '0.85rem' }}>₦{e.snap.current.toLocaleString()}</Typography>
                        <Typography sx={{ fontSize: '0.66rem', color: palette.textMuted }}>
                          {e.snap.bagSize}kg bag · avg of {e.snap.markets} market{e.snap.markets === 1 ? '' : 's'}
                        </Typography>
                      </>
                    ) : (
                      <Typography sx={{ color: palette.textMuted }}>-</Typography>
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ fontFamily: MONO, fontWeight: 700 }}>
                    {e.value != null ? `₦${e.value.toLocaleString()}` : '-'}
                  </TableCell>
                  <TableCell align="right">
                    {e.snap.current != null ? (
                      <DeltaChip value={e.snap.change} decimals={2} />
                    ) : (
                      <Typography sx={{ color: palette.textMuted }}>-</Typography>
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ fontFamily: MONO, fontWeight: 700, color: e.pnl == null ? palette.textMuted : e.pnl >= 0 ? palette.accent : palette.danger }}>
                    {e.pnl != null ? `${e.pnl >= 0 ? '+' : ''}₦${Math.round(e.pnl).toLocaleString()}` : '-'}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      aria-label={`Remove ${e.commodity?.name || 'holding'} from portfolio`}
                      onClick={() => setConfirmId(e.id)}
                      sx={{ color: palette.danger }}
                    >
                      <Icon icon="ph:trash" width={16} height={16} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Box sx={{ px: 2, py: 1.25, borderTop: `1px solid ${palette.divider}`, bgcolor: palette.surface }}>
            <Typography sx={{ fontSize: '0.7rem', color: palette.textMuted }}>
              Valuations use the average price across all reporting markets on each commodity's
              latest collection date, for its most common bag size. No single market is assumed.
            </Typography>
          </Box>
        </TableContainer>
      )}

      {/* Add holding dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={recipes.dialogPaperProps}
      >
        <DialogTitle sx={{ color: palette.text, fontWeight: 700 }}>Add commodity to portfolio</DialogTitle>
        <DialogContent dividers sx={{ borderColor: palette.divider }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <FilterSelect
              label="Commodity"
              value={form.commodityId}
              onChange={(v) => setForm({ ...form, commodityId: v })}
              options={pickerCommodities.map((c) => ({ value: c.id, label: c.name }))}
            />

            {/* Current price for the selection - the valuation basis */}
            <Box
              sx={{
                px: 1.5,
                py: 1.1,
                borderRadius: '10px',
                bgcolor: palette.surface,
                border: `1px solid ${palette.cardBorder}`,
              }}
            >
              <Typography sx={{ fontSize: '0.66rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: palette.textMuted }}>
                Current price
              </Typography>
              {dialogSnap?.current != null ? (
                <>
                  <Typography sx={{ fontFamily: MONO, fontWeight: 700, fontSize: '1rem', color: palette.text }}>
                    ₦{dialogSnap.current.toLocaleString()}
                    <Typography component="span" sx={{ fontFamily: MONO, fontSize: '0.72rem', color: palette.textMuted, ml: 0.5 }}>
                      per {dialogSnap.bagSize}kg bag · avg of {dialogSnap.markets} market{dialogSnap.markets === 1 ? '' : 's'}
                    </Typography>
                  </Typography>
                  <UpdatedStamp date={dialogSnap.date} />
                  {estimatedValue != null && (
                    <Typography sx={{ fontSize: '0.78rem', color: palette.textSecondary, mt: 0.75 }}>
                      Estimated value:{' '}
                      <Box component="span" sx={{ fontFamily: MONO, fontWeight: 700, color: palette.text }}>
                        ₦{estimatedValue.toLocaleString()}
                      </Box>{' '}
                      for {Number(form.quantity).toLocaleString()} bag{Number(form.quantity) === 1 ? '' : 's'}
                    </Typography>
                  )}
                </>
              ) : (
                <Typography sx={{ fontSize: '0.8rem', color: palette.textMuted }}>
                  No recent price data for this commodity
                </Typography>
              )}
            </Box>

            <TextField
              label="Quantity (number of bags)"
              type="number"
              size="small"
              fullWidth
              value={form.quantity}
              onChange={(e) => setForm({ ...form, quantity: e.target.value })}
              sx={recipes.searchSx}
              InputLabelProps={{ sx: recipes.labelSx }}
              InputProps={{ sx: { fontFamily: MONO } }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} sx={{ color: palette.textMuted, textTransform: 'none' }}>Cancel</Button>
          <Button onClick={save} variant="contained" disableElevation sx={ctaSx}>
            Add to portfolio
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove confirmation */}
      <ConfirmDialog
        open={!!confirmId}
        onClose={() => setConfirmId(null)}
        onConfirm={() => { remove(confirmId); setConfirmId(null); }}
        title="Remove this holding?"
        description={
          confirmTarget
            ? `${confirmTarget.commodity?.name || 'This commodity'} (${confirmTarget.quantity.toLocaleString()} bags) will be removed from your portfolio. Price history is not affected.`
            : 'This holding will be removed from your portfolio.'
        }
        confirmLabel="Remove holding"
        tone="danger"
        icon="ph:trash"
      />

      <Snackbar open={!!toast} autoHideDuration={3500} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        {toast && <MuiAlert severity={toast.severity} onClose={() => setToast(null)}>{toast.msg}</MuiAlert>}
      </Snackbar>
    </Box>
  );
}

export default Portfolio;
